/**
 * ═══════════════════════════════════════════════════════════════════════
 * ФОРМАТЫ ЧИСЕЛ И ДАТ — ОДИН РАЗ НА НАБОР НАСТРОЕК, А НЕ НА КАЖДОЕ ЧИСЛО.
 *
 * ЗАЧЕМ ЭТО ЗАВЕДЕНО. Владелец сказал: кабинет стал тяжелее, а работал
 * быстро. Одна из двух найденных причин — здесь.
 *
 * `new Intl.NumberFormat(...)` — не «создать объектик». Это разбор
 * названия локали, поиск данных о ней и сборка правил разрядов; в узле
 * замер дал 32,5 мкс против 0,7 мкс у готового формата — В ПЯТЬДЕСЯТ РАЗ
 * дороже самого форматирования. В браузере разрыв не меньше.
 *
 * А создавался он на КАЖДУЮ ЯЧЕЙКУ. `число()` в таблице кампаний звало
 * `дробью(знаков)`, и та собирала новый формат на каждый процент
 * и на каждую дробную сумму: полсотни строк на восьми числовых столбцах —
 * это две сотни сборок, шесть-семь миллисекунд, и так на КАЖДУЮ
 * перерисовку: наведение, сортировка, отбор, фоновое обновление.
 * На экране запросов было хуже: `числамиСтрокой` собирала ДВА формата
 * на строку, а строк там сотни.
 *
 * То же самое прячется за `toLocaleString(locale, options)` — он по
 * стандарту делает ровно то же: создаёт формат и зовёт `format`.
 * Поэтому и такие места переведены сюда.
 *
 * ЧТО ЗДЕСЬ. Хранилище готовых форматов с ключом по настройкам. Ключ —
 * `JSON.stringify` настроек: их у нас единицы, а сравнивать объекты
 * по содержимому иначе нечем. Собранный формат живёт до конца вкладки:
 * форматы не держат ничего тяжёлого и не устаревают.
 *
 * ЧЕГО ЗДЕСЬ НЕТ И НЕ БУДЕТ: своей арифметики округления и своих
 * разделителей разрядов. Числа в кабинете — деньги продавца, и считает
 * их `Intl`, как считал. Меняется только то, СКОЛЬКО РАЗ он собирается.
 * ═══════════════════════════════════════════════════════════════════════
 */

/** Единственная локаль кабинета. Отдельным именем, чтобы не разъехалась. */
export const РУ = 'ru-RU';

const числа = new Map<string, Intl.NumberFormat>();
const даты = new Map<string, Intl.DateTimeFormat>();

/**
 * Готовый формат числа под эти настройки.
 *
 * Зовите его прямо в разметке: второй вызов с теми же настройками
 * ничего не собирает.
 */
export function форматЧисла(опции?: Intl.NumberFormatOptions): Intl.NumberFormat {
  const ключ = опции ? JSON.stringify(опции) : '';
  let f = числа.get(ключ);
  if (f === undefined) {
    f = new Intl.NumberFormat(РУ, опции);
    числа.set(ключ, f);
  }
  return f;
}

/** Готовый формат даты под эти настройки. Довод — тот же. */
export function форматДаты(опции?: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const ключ = опции ? JSON.stringify(опции) : '';
  let f = даты.get(ключ);
  if (f === undefined) {
    f = new Intl.DateTimeFormat(РУ, опции);
    даты.set(ключ, f);
  }
  return f;
}

/**
 * Число с заданным числом знаков после запятой.
 *
 * Самая частая нужда таблиц: проценты с одним знаком, деньги с двумя,
 * ставки с двумя. Отдельным именем, чтобы на каждом месте не повторять
 * пару `minimumFractionDigits`/`maximumFractionDigits`, — и чтобы
 * ключ у них совпадал, а формат был один на всех.
 */
export function знаками(знаков: number): Intl.NumberFormat {
  return форматЧисла({ minimumFractionDigits: знаков, maximumFractionDigits: знаков });
}

/** Целые: разряды через пробел, дроби отброшены. */
export const целые = форматЧисла({ maximumFractionDigits: 0 });

/** Деньги в таблицах: две цифры после запятой всегда. */
export const деньгами = знаками(2);
